import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { Loading, Card } from "@/components/Shared";
import { RecordLayout, DetailPanel, Field } from "@/components/Record";
import { RecordTabs } from "@/components/Activities";
import { ProductThumb } from "@/components/QuoteParts";
import BinsDialog from "@/components/inventory/BinsDialog";
import { formatINR } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/sonner";
import { ChevronLeft, Warehouse, Boxes, MapPin, History, ChevronRight } from "lucide-react";

const fdt = (s) => (s ? new Date(s).toLocaleString("en-IN") : "—");
const Stat = ({ label, value }) => (<div><div className="text-[11px] text-[#6B7189]">{label}</div><div className="text-lg font-bold text-[#101D51]">{value}</div></div>);

export default function WarehouseDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [tab, setTab] = useState("stock");
  const [bins, setBins] = useState(false);
  const load = () => api.get(`/warehouses/${id}/detail`).then((r) => setD(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, [id]);
  if (!d) return <Loading />;
  const w = d.warehouse, st = d.stats;
  const tabs = [
    { id: "stock", label: "Stock", icon: Boxes, count: d.stock.length },
    { id: "bins", label: "Bins", icon: MapPin, count: d.bins.length },
    { id: "movements", label: "Movements", icon: History, count: d.movements.length },
  ];
  return (
    <div data-testid="warehouse-detail">
      <button onClick={() => navigate("/inventory?tab=warehouses")} className="flex items-center gap-1 text-sm text-[#101D51] mb-3" data-testid="wh-back"><ChevronLeft className="w-4 h-4" /> Warehouses</button>
      <div className="bg-white border border-[#E6E8F0] rounded-xl px-4 sm:px-5 py-3 mb-4 shadow-sm flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-[#101D51] flex items-center justify-center text-white shrink-0"><Warehouse className="w-5 h-5" /></div>
        <div className="min-w-0 flex-1">
          <div className="text-xs text-[#6B7189]">Warehouse</div>
          <h1 className="text-xl sm:text-2xl font-bold text-[#101D51] truncate">{w.name}{w.is_default && <span className="ml-2 text-[10px] bg-[#EAF3FF] text-[#1D4ED8] rounded-full px-2 py-0.5 align-middle">Default</span>}</h1>
        </div>
      </div>
      <RecordLayout
        left={<>
          <DetailPanel title="Warehouse">
            <Field label="Name" value={w.name} />
            <Field label="Code" value={w.code} />
            <Field label="Default" value={w.is_default ? "Yes" : "No"} />
            <Field label="Address" value={w.address} />
          </DetailPanel>
          <Card className="p-4 grid grid-cols-2 gap-3" data-testid="wh-stats">
            <Stat label="Products" value={st.products} />
            <Stat label="Total units" value={st.total_units} />
            <Stat label="Stock value" value={formatINR(st.stock_value_paise)} />
            <Stat label="Low / Out" value={`${st.low_stock} / ${st.out_of_stock}`} />
          </Card>
        </>}
        right={<>
          <RecordTabs tabs={tabs} active={tab} onChange={setTab} testid="wh-tabs" />
          {tab === "stock" && (
            <Card className="overflow-hidden" data-testid="wh-stock">
              {d.stock.length === 0 ? <div className="p-5 text-sm text-[#6B7189]">No stock in this warehouse yet.</div> : (
                <div className="overflow-x-auto"><table className="w-full text-sm">
                  <thead className="bg-[#F5F6FA] text-[#6B7189]"><tr className="text-left">
                    <th className="px-4 py-2 font-medium w-12"></th><th className="px-4 py-2 font-medium">Product</th>
                    <th className="px-4 py-2 font-medium">Bin</th><th className="px-4 py-2 font-medium text-right">Available</th>
                    <th className="px-4 py-2 font-medium text-right">Reserved</th><th className="px-4 py-2 font-medium text-right">Reorder</th><th className="px-4 py-2"></th>
                  </tr></thead>
                  <tbody>{d.stock.map((s) => (
                    <tr key={s.product_id + (s.bin?.id || "")} className="border-t border-[#F0F1F5] hover:bg-[#F5F6FA] cursor-pointer" onClick={() => navigate(`/products/${s.product_id}`)} data-testid={`wh-stock-${s.code}`}>
                      <td className="px-4 py-2"><ProductThumb url={s.image_url} size={8} /></td>
                      <td className="px-4 py-2"><div className="font-medium text-[#101D51]">{s.name}</div><div className="text-[11px] text-[#6B7189] font-mono">{s.code}</div></td>
                      <td className="px-4 py-2"><span className="font-mono text-xs bg-[#F5F6FA] rounded px-1.5 py-0.5">{s.bin?.code || "—"}</span></td>
                      <td className="px-4 py-2 text-right font-semibold">{s.available} {s.unit}</td>
                      <td className="px-4 py-2 text-right text-[#6B7189]">{s.reserved}</td>
                      <td className="px-4 py-2 text-right text-[#6B7189]">{s.reorder_level}</td>
                      <td className="px-4 py-2 text-right"><ChevronRight className="w-4 h-4 text-[#6B7189]" /></td>
                    </tr>))}</tbody>
                </table></div>)}
            </Card>)}
          {tab === "bins" && (
            <Card className="p-4" data-testid="wh-bins">
              <div className="flex items-center justify-between mb-3"><div className="font-semibold text-[#101D51]">Bins ({d.bins.length})</div><Button variant="outline" className="h-9" onClick={() => setBins(true)} data-testid="wh-manage-bins"><MapPin className="w-4 h-4" /> Manage bins</Button></div>
              <div className="space-y-1.5">{d.bins.map((b) => (
                <div key={b.id} className="flex items-center justify-between bg-[#F8F9FC] rounded-lg px-3 py-2 text-sm" data-testid={`wh-bin-${b.id}`}>
                  <span className="text-[#101D51]"><span className="font-mono text-xs bg-white border border-[#E6E8F0] rounded px-1.5 py-0.5 mr-2">{b.code}</span>{b.name}</span>
                  {b.is_default && <span className="text-[10px] text-[#1D4ED8]">default</span>}
                </div>))}</div>
            </Card>)}
          {tab === "movements" && (
            <Card className="p-4" data-testid="wh-movements">
              {d.movements.length === 0 ? <div className="text-sm text-[#6B7189]">No movements recorded.</div> : (
                <div className="divide-y divide-[#F0F1F5]">{d.movements.map((m) => (
                  <div key={m.id} className="flex items-center justify-between py-2 text-sm">
                    <div className="min-w-0"><div className="text-[#101D51] truncate">{m.product?.name || m.product_id} <span className="text-[#6B7189]">· {m.movement_type}</span></div>
                      <div className="text-[11px] text-[#6B7189]">{m.bin?.code ? `${m.bin.code} · ` : ""}{fdt(m.created_at)}{m.notes ? ` · ${m.notes}` : ""}</div></div>
                    <span className={`font-semibold shrink-0 ${m.quantity >= 0 ? "text-[#0B7C5A]" : "text-[#B91C1C]"}`}>{m.quantity >= 0 ? "+" : ""}{m.quantity}</span>
                  </div>))}</div>)}
            </Card>)}
        </>} />
      {bins && <BinsDialog warehouse={w} onClose={() => { setBins(false); load(); }} />}
    </div>);
}
